import type { Trip, TripAssignmentStatus, TripSource } from '../types/database';
import { hasFullCrew } from './crewAssignment';

/** PostgREST filter: driver-visible trips (owner/hq always; platform only when accepted). */
export const DRIVER_VISIBLE_TRIP_OR_FILTER =
  'source.neq.platform,assignment_status.eq.accepted';

export function isDriverVisibleTrip(
  trip: Pick<Trip, 'source' | 'assignment_status'>
): boolean {
  const source = trip.source ?? 'hq';
  if (source !== 'platform') return true;
  return trip.assignment_status === 'accepted';
}

/** GPS tracking applies only to platform trips accepted by the fleet owner. */
export function isGpsTrackableTrip(
  trip: Pick<Trip, 'source' | 'assignment_status'>
): boolean {
  return trip.source === 'platform' && trip.assignment_status === 'accepted';
}

export function formatClientTripDisplayStatus(
  trip: Pick<Trip, 'status' | 'source' | 'assignment_status' | 'vehicle_id' | 'driver_id'>
): string {
  if (
    trip.source === 'platform' &&
    trip.assignment_status === 'pending_owner' &&
    trip.status === 'scheduled'
  ) {
    return 'Awaiting fleet owner';
  }
  if (trip.source === 'platform' && trip.assignment_status === 'rejected') {
    return 'Reassigning';
  }
  if (trip.status === 'scheduled' && trip.vehicle_id && !trip.driver_id) {
    return 'Vehicle assigned · Driver pending';
  }
  if (trip.status === 'dispatched' && hasFullCrew(trip)) {
    return 'Crew assigned';
  }
  if (trip.status === 'dispatched' && trip.vehicle_id && !trip.driver_id) {
    return 'Vehicle assigned · Driver pending';
  }
  const statusMap: Record<string, string> = {
    scheduled: 'Scheduled',
    dispatched: 'Dispatched',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return statusMap[trip.status] ?? trip.status;
}

export function getClientTripDisplayColor(
  trip: Pick<Trip, 'status' | 'source' | 'assignment_status'>
): string {
  if (trip.source === 'platform' && trip.assignment_status === 'pending_owner') {
    return 'gold';
  }
  if (trip.source === 'platform' && trip.assignment_status === 'rejected') {
    return 'orange';
  }
  const colorMap: Record<string, string> = {
    scheduled: 'blue',
    dispatched: 'cyan',
    in_progress: 'processing',
    completed: 'success',
    cancelled: 'error',
  };
  return colorMap[trip.status] ?? 'default';
}

export function formatTripSourceLabel(source?: TripSource | null): string {
  switch (source) {
    case 'owner':
      return 'Owner schedule';
    case 'platform':
      return 'Platform order';
    case 'hq':
    default:
      return 'HQ dispatch';
  }
}

export function formatAssignmentStatusLabel(
  status?: TripAssignmentStatus | null
): string | null {
  switch (status) {
    case 'pending_owner':
      return 'Pending owner';
    case 'accepted':
      return 'Accepted';
    case 'rejected':
      return 'Rejected';
    default:
      return null;
  }
}
